import { populateRelationships } from '~/utils/api/v1'
import { useFetchWithDefaults } from '~/composables/api/useFetchWithDefaults'

import type { SignableContract } from '../types'
import type { WorkspaceIdentifier } from '../../types'
import type { InviteSignaturePayload, UpdateSignaturePayload } from './types'

import {
  type ContractIdentifier,
  defaultIncludeParams,
  getAllUrl,
  getByIdUrl as getContractByIdUrl,
} from '../useFetchContract'

export type SignatureIdentifier = ContractIdentifier & {
  signatureId: string
}

export const useFetchDocumentSignature = () => {
  const getUrl = ({ contractId, signatureId }: { contractId: string, signatureId: string }) =>
    `${getContractByIdUrl(contractId)}/${signatureId}`

  const { put, post } = useFetchWithDefaults()

  const update = (
    {
      payload,
      signatureId,
      contractId,
      workspaceId,
    }:
    SignatureIdentifier & { payload: UpdateSignaturePayload },
  ) =>
    put<void>({ // something is returned, but since this is v1 and therefore subject to change the type was omitted here
      url: getUrl({ contractId, signatureId }),
      workspaceId,
      fetchOptions: {
        body: payload,
      },
    })

  const invite = ({
    payload,
    signatureId,
    contractId,
    workspaceId,
  }:
    SignatureIdentifier & { payload: InviteSignaturePayload },
  ) => post<void>({
    // something is returned, but since this is v1 and therefore subject to change the type was omitted here
    url: `${getUrl({ contractId, signatureId })}/invite`,
    workspaceId,
    fetchOptions: {
      body: payload,
    },
  })

  const cancel = ({ contractId, signatureId, workspaceId }: SignatureIdentifier) =>
    post<void>({
      url: `${getUrl({ contractId, signatureId })}/cancel`,
      workspaceId,
    })

  const inviteAllSignees = ({ contractId, workspaceId }: WorkspaceIdentifier & { contractId: string }) =>
    post<void>({
      url: `${getAllUrl}/remind`,
      workspaceId,
      fetchOptions: {
        body: {
          contractId,
        },
      },
    })

  const reopen = ({
    contractId,
    workspaceId,
  }: ContractIdentifier) =>
    post<void>({
      url: `${getContractByIdUrl(contractId)}/reopen`,
      workspaceId,
    })

  const sign = ({
    contractId,
    signatureId,
    workspaceId,
    payload,
  }: SignatureIdentifier & { payload: string }) =>
    post<{ data: SignableContract }>({
      url: `${getUrl({ contractId, signatureId })}/sign`,
      workspaceId,
      fetchOptions: {
        params: defaultIncludeParams,
        body: { signatureData: payload },
      },
    }).then(populateRelationships)

  return {
    sign,
    update,
    invite,
    cancel,
    inviteAllSignees,
    reopen,
  }
}
